import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQueries } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import type { Quote } from '@furni/shared';
import { Button } from '../components/Button';
import { Card, Ornament } from '../components/Card';
import { NoPaymentNote } from '../components/NoPaymentNote';
import { Shell } from '../components/Shell';
import { fetchQuote } from '../lib/api';
import { cart, useCart } from '../lib/cart';
import { formatNumber, formatSAR } from '../lib/format';
import { useSettings } from '../lib/settings';

export const Route = createFileRoute('/cart')({
  component: CartPage,
});

function CartPage() {
  const { t } = useTranslation();
  const items = useCart();
  const navigate = useNavigate();
  const settings = useSettings();
  const VAT_RATE = settings['pricing.vatRate'] / 100;
  const discountTiers = settings['pricing.quantityDiscountTiers'];
  const deliveryLabel = settings['delivery.label'];
  const deliveryFee = settings['delivery.fee'];

  const quoteQueries = useQueries({
    queries: items.map((item) => ({
      queryKey: ['quote', item.quote_id],
      queryFn: () => fetchQuote(item.quote_id),
    })),
  });

  const loaded = quoteQueries.every((q) => q.isSuccess);
  const quotes = quoteQueries
    .map((q) => q.data)
    .filter((q): q is Quote => Boolean(q));

  if (items.length === 0) {
    return (
      <Shell>
        <div className="pt-20 flex flex-col items-center text-center space-y-5">
          <EmptyCartIcon />
          <h1 className="text-2xl font-black text-oud-900">{t('cart.empty_title')}</h1>
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-2xl font-bold h-12 px-6 bg-oud-800 text-sand hover:bg-oud-900 transition-all"
          >
            {t('cart.empty_cta')}
          </Link>
        </div>
      </Shell>
    );
  }

  if (!loaded) {
    return (
      <Shell showBack>
        <div className="pt-6 space-y-3">
          {items.map((i) => (
            <div key={i.quote_id} className="shimmer-bg animate-shimmer h-24 rounded-3xl" />
          ))}
        </div>
      </Shell>
    );
  }

  const itemsWithQuotes = items
    .map((item) => {
      const quote = quotes.find((q) => q.id === item.quote_id);
      return quote ? { item, quote } : null;
    })
    .filter((x): x is { item: typeof items[number]; quote: Quote } => x !== null);

  const minQty = settings['pricing.minQuantity'] ?? 1;
  const minOrderTotal = settings['pricing.minOrderTotalSAR'] ?? 0;

  const subtotal = itemsWithQuotes.reduce(
    (sum, { item, quote }) => sum + item.qty * quote.custom_price_sar,
    0,
  );
  const totalQty = itemsWithQuotes.reduce((sum, { item }) => sum + item.qty, 0);
  const pieceCount = itemsWithQuotes.length;
  const discountPct = [...discountTiers]
    .sort((a, b) => b.minPieces - a.minPieces)
    .find((t) => pieceCount >= t.minPieces)?.pct ?? 0;
  const discountAmount = Math.round((subtotal * discountPct) / 100);
  const afterDiscount = subtotal - discountAmount;
  const vat = Math.round(afterDiscount * VAT_RATE);
  const total = afterDiscount + vat + deliveryFee;

  const belowMinQty = minQty > 1 && totalQty < minQty;
  const belowMinTotal = minOrderTotal > 0 && total < minOrderTotal;
  const canCheckout = !belowMinQty && !belowMinTotal;

  return (
    <Shell showBack>
      <div className="pt-4 pb-6 space-y-6">
        <div>
          <h1 className="text-2xl font-black text-oud-900">{t('cart.title')}</h1>
        </div>

        <div className="space-y-3">
          {itemsWithQuotes.map(({ item, quote }) => (
            <CartItemRow
              key={item.quote_id}
              quote={quote}
              qty={item.qty}
              notes={item.notes}
              onRemove={() => cart.remove(item.quote_id)}
            />
          ))}
        </div>

        <Link
          to="/"
          className="block text-center py-3 text-sm text-oud-700 hover:text-oud-900 font-bold border border-dashed border-oud-200 rounded-2xl"
        >
          + {t('cart.scan_another')}
        </Link>

        <div className="flex items-center gap-3">
          <Ornament className="flex-1" />
          <span className="w-1.5 h-1.5 rounded-full bg-gold" />
          <Ornament className="flex-1" />
        </div>

        <div className="space-y-2.5">
          <TotalRow label={t('cart.subtotal')} value={formatSAR(subtotal)} />
          {discountPct > 0 ? (
            <TotalRow
              label={t('cart.discount', { pct: discountPct })}
              value={`− ${formatSAR(discountAmount)}`}
              accent="sage"
            />
          ) : null}
          <TotalRow label={t('cart.vat')} value={formatSAR(vat)} />
          <TotalRow
            label={t('cart.delivery')}
            value={deliveryFee > 0 ? formatSAR(deliveryFee) : deliveryLabel}
            accent="gold"
          />

          <div className="pt-3 border-t border-oud-100/60">
            <div className="flex items-baseline justify-between">
              <span className="text-sm font-bold text-oud-700">{t('cart.total_estimate')}</span>
              <motion.span
                key={total}
                initial={{ scale: 0.95, opacity: 0.6 }}
                animate={{ scale: 1, opacity: 1 }}
                className="text-3xl font-black text-oud-900 tabular-nums"
              >
                {formatSAR(total)}
              </motion.span>
            </div>
          </div>
        </div>

        <NoPaymentNote />

        {belowMinQty ? (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3 text-center">
            <p className="text-sm text-amber-800 font-bold">
              الحد الأدنى للطلب {formatNumber(minQty)} قطع — لديك حاليًا {formatNumber(totalQty)}
            </p>
            <p className="text-xs text-amber-700 mt-1">أضف المزيد من القطع للمتابعة</p>
          </div>
        ) : null}

        {belowMinTotal ? (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3 text-center">
            <p className="text-sm text-amber-800 font-bold">
              الحد الأدنى لإجمالي الطلب {formatSAR(minOrderTotal)} — الإجمالي الحالي {formatSAR(total)}
            </p>
            <p className="text-xs text-amber-700 mt-1">أضف المزيد من القطع للمتابعة</p>
          </div>
        ) : null}

        <div className="pt-2">
          <Button
            size="lg"
            fullWidth
            onClick={() => void navigate({ to: '/checkout' })}
            disabled={!canCheckout}
          >
            {t('cart.submit')}
          </Button>
        </div>
      </div>
    </Shell>
  );
}

function CartItemRow({
  quote,
  qty,
  notes,
  onRemove,
}: {
  quote: Quote;
  qty: number;
  notes?: string;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Card className="flex items-stretch gap-0 p-0 overflow-hidden hover:border-oud-200 transition-colors">
      <Link
        to="/quote/$id"
        params={{ id: quote.id }}
        className="flex items-stretch gap-3 flex-1 min-w-0"
      >
        <img
          src={quote.image_url}
          alt=""
          className="flex-none w-24 object-cover bg-oud-50"
        />
        <div className="flex-1 min-w-0 py-3 pl-3 flex flex-col justify-center">
          <h4 className="font-bold text-oud-900 text-sm line-clamp-1 leading-snug">
            {quote.analysis.type}
            {quote.analysis.style ? (
              <span className="font-normal text-oud-600"> · {quote.analysis.style}</span>
            ) : null}
          </h4>
          <div className="flex items-baseline gap-2 mt-0.5">
            <p className="text-[11px] text-oud-600">
              {t('cart.item_qty', { qty: formatNumber(qty) })}
            </p>
            <span className="text-oud-300">·</span>
            <p className="text-sm font-bold text-gold-deep">
              {formatSAR(quote.custom_price_sar)}
            </p>
          </div>
          {notes ? (
            <p className="text-[11px] text-oud-600 line-clamp-2 mt-1 italic">{notes}</p>
          ) : null}
        </div>
      </Link>
      <button
        type="button"
        onClick={onRemove}
        aria-label={t('cart.remove')}
        className="flex-none px-3 text-oud-400 hover:text-red-800"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4" aria-hidden>
          <path d="M3 6h18M8 6V4h8v2M6 6v14a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V6" />
        </svg>
      </button>
    </Card>
  );
}

function TotalRow({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: 'sage' | 'gold';
}) {
  const valueClass =
    accent === 'sage' ? 'text-sage-deep' : accent === 'gold' ? 'text-gold-deep' : 'text-oud-900';
  return (
    <div className="flex items-baseline justify-between text-sm">
      <span className="text-oud-700">{label}</span>
      <span className={`font-bold tabular-nums ${valueClass}`}>{value}</span>
    </div>
  );
}

function EmptyCartIcon() {
  return (
    <div className="w-20 h-20 rounded-full bg-cream border border-oud-100 flex items-center justify-center text-oud-300">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" className="w-9 h-9" aria-hidden>
        <path d="M3 3h2l2.4 12a2 2 0 0 0 2 1.6h8.2a2 2 0 0 0 2-1.6L21 7H6" />
        <circle cx="10" cy="20" r="1.5" />
        <circle cx="18" cy="20" r="1.5" />
      </svg>
    </div>
  );
}
